/**
 * Campus Valley 360° — Landing
 * Refonte V20 : hub d'apprentissage avec 10 Piliers (tronc commun) + 14 Cas d'usage.
 * Conserve la compatibilité avec les anciens liens via les use cases.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
    GraduationCap, Compass, Banknote, TrendingUp, Network, ShieldAlert,
    FileSignature, BarChart3, Presentation, Leaf, Archive, ArrowRight,
    Sparkles, Clock, Award, Layers, Download, Loader2,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { log } from '@/lib/log';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// lucide icon resolver — falls back to Layers if unknown
const ICON_MAP = {
    Compass, Banknote, TrendingUp, Network, ShieldAlert,
    FileSignature, BarChart3, PresentationChart: Presentation, Leaf, Archive, Layers,
};
const PillarIcon = ({ name, ...rest }) => {
    const Icon = ICON_MAP[name] || Layers;
    return <Icon {...rest} />;
};

export default function Formation() {
    const { lang } = useLang();
    const { session, user, loading: authLoading } = useAuth();
    const [pillars, setPillars] = useState([]);
    const [useCases, setUseCases] = useState([]);
    const [progress, setProgress] = useState(null);
    const [popular, setPopular] = useState([]);
    const { currentWorkspace } = useWorkspace();
    const [teamGoal, setTeamGoal] = useState(null);
    const [goalDialog, setGoalDialog] = useState(false);
    const [goalInput, setGoalInput] = useState('');
    const [savingGoal, setSavingGoal] = useState(false);

    useEffect(() => {
        if (authLoading || !session || !currentWorkspace) return;
        axios.get(`${API}/campus/team-goal`, {
            params: { workspace_id: currentWorkspace.id },
            headers: { Authorization: `Bearer ${session.access_token}` },
        }).then(r => setTeamGoal(r.data)).catch(() => setTeamGoal(null));
    }, [authLoading, session, currentWorkspace?.id]);

    const saveGoal = async () => {
        const val = parseInt(goalInput, 10);
        if (Number.isNaN(val) || val < 0) { toast.error(lang === 'fr' ? 'Saisissez un nombre valide.' : 'Enter a valid number.'); return; }
        setSavingGoal(true);
        try {
            await axios.post(`${API}/campus/team-goal`,
                { workspace_id: currentWorkspace.id, monthly_goal: val },
                { headers: { Authorization: `Bearer ${session.access_token}` } });
            setTeamGoal(g => ({ ...g, goal: val }));
            setGoalDialog(false);
            toast.success(lang === 'fr' ? `Objectif fixé : ${val} leçons / mois` : `Goal set: ${val} lessons / month`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setSavingGoal(false);
    };
    const [resumeLessons, setResumeLessons] = useState([]);
    useEffect(() => {
        try {
            const items = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith('av-lesson-pos-')) {
                    try {
                        const v = JSON.parse(localStorage.getItem(k));
                        if (v && typeof v === 'object' && v.pct && v.t) {
                            items.push({ id: k.slice('av-lesson-pos-'.length), pct: v.pct, title: v.t });
                        }
                    } catch { /* old format */ }
                }
            }
            setResumeLessons(items.sort((a, b) => b.pct - a.pct).slice(0, 4));
        } catch { /* ignore */ }
    }, []);
    const [loading, setLoading] = useState(true);
    const [downloadingCoreCert, setDownloadingCoreCert] = useState(false);

    const downloadCoreCert = async () => {
        setDownloadingCoreCert(true);
        try {
            const r = await axios.get(`${API}/campus/certificate-core.pdf`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `campus-tronc-commun.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(lang === 'fr' ? 'Certificat Tronc Commun téléchargé !' : 'Core certificate downloaded!');
        } catch (e) {
            const detail = e.response?.data instanceof Blob
                ? await e.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
                : e.response?.data?.detail || e.message;
            toast.error(detail || 'Erreur');
        }
        setDownloadingCoreCert(false);
    };

    useEffect(() => {
        if (authLoading || !session) return;
        (async () => {
            try {
                const headers = { Authorization: `Bearer ${session.access_token}` };
                const [pr, ur, mp, pop] = await Promise.all([
                    axios.get(`${API}/campus/pillars`, { headers }),
                    axios.get(`${API}/campus/use-cases`, { headers }),
                    axios.get(`${API}/campus/my-progress`, { headers }),
                    axios.get(`${API}/campus/popular`, { headers }).catch(() => ({ data: { items: [] } })),
                ]);
                setPillars(pr.data.items || []);
                setUseCases(ur.data.items || []);
                setProgress(mp.data);
                setPopular(pop.data.items || []);
            } catch (e) {
                log.error('Campus load error', e);
            } finally {
                setLoading(false);
            }
        })();
    }, [authLoading, session, user]);

    const progressByPillar = (progress?.pillars || []).reduce(
        (acc, p) => ({ ...acc, [p.pillar_id]: p }), {}
    );

    return (
        <div className="space-y-12" data-testid="campus-landing">
            {/* HERO */}
            <section className="relative overflow-hidden rounded-3xl text-white p-10 lg:p-14"
                     style={{ background: 'linear-gradient(135deg, #1f1a16 0%, #2a221c 50%, #3a302a 100%)' }}>
                {/* Aurora layers */}
                <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full kiosk-aurora opacity-70"
                     style={{ background: 'radial-gradient(closest-side, rgba(212,114,47,0.55), transparent 70%)' }} />
                <div className="absolute -bottom-32 -left-32 h-80 w-80 rounded-full kiosk-aurora opacity-50"
                     style={{ background: 'radial-gradient(closest-side, rgba(57,99,76,0.5), transparent 70%)', animationDelay: '-7s' }} />
                {/* Grain texture */}
                <div className="absolute inset-0 mix-blend-overlay opacity-[0.12] pointer-events-none"
                     style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />

                <div className="relative">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-[11px] uppercase tracking-[0.3em] text-primary mb-5 ring-1 ring-white/10">
                        <Sparkles size={12} /> Campus Valley 360°
                    </div>
                    <h1 className="font-heading text-5xl lg:text-6xl leading-[1.05] mb-5 text-white max-w-4xl">
                        {lang === 'fr'
                            ? <>L'académie africaine du <span className="kiosk-shimmer-text">pilotage projet</span>.</>
                            : <>The African academy of <span className="kiosk-shimmer-text">project steering</span>.</>}
                    </h1>
                    <p className="text-white/75 max-w-2xl text-lg leading-relaxed">
                        {lang === 'fr'
                            ? '10 piliers méthodologiques + 14 cas d\'usage sectoriels — vidéos, audios, supports PDF/Excel, quiz et assistant IA pour former vos équipes aux standards internationaux.'
                            : '10 methodological pillars + 14 sectoral use cases — videos, audios, PDF/Excel materials, quizzes and AI tutor to train your teams to international standards.'}
                    </p>
                    {progress && (
                        <div className="mt-10 flex flex-wrap items-end gap-8 lg:gap-12">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.25em] text-white/55 mb-2">
                                    {lang === 'fr' ? 'Votre progression — Tronc commun' : 'Your progress — Core'}
                                </div>
                                <div className="flex items-baseline gap-3">
                                    <span className="font-heading text-6xl text-white tabular-nums">{progress.tronc_commun_percent}<span className="text-primary text-4xl">%</span></span>
                                    <span className="text-white/55 text-sm">{progress.lessons_completed}/{progress.lessons_total} {lang === 'fr' ? 'leçons' : 'lessons'}</span>
                                </div>
                                <div className="mt-3 w-80 h-2 rounded-full bg-white/10 overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-primary to-amber-400 transition-all duration-1000 shadow-[0_0_18px_rgba(212,114,47,0.6)]"
                                         style={{ width: `${progress.tronc_commun_percent || 2}%` }} />
                                </div>
                            </div>
                            {progress.tronc_commun_percent === 100 ? (
                                <Button onClick={downloadCoreCert} disabled={downloadingCoreCert}
                                        className="bg-white text-[#1f1a16] hover:bg-white/90 font-medium px-6" data-testid="core-cert-download-hero">
                                    {downloadingCoreCert ? <Loader2 size={14} className="animate-spin mr-2" /> : <Award size={14} className="mr-2 text-primary" />}
                                    {lang === 'fr' ? 'Certificat Tronc Commun' : 'Core certificate'}
                                </Button>
                            ) : (
                                <div className="text-xs text-white/45 leading-relaxed max-w-xs italic">
                                    {lang === 'fr'
                                        ? 'Validez les 10 piliers pour débloquer votre certificat Tronc Commun premium 🏆'
                                        : 'Complete all 10 pillars to unlock your premium Core certificate 🏆'}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* OBJECTIF FORMATION ÉQUIPE */}
            {teamGoal && (
                <section className="rounded-2xl border border-border bg-white p-5" data-testid="campus-team-goal-section">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-secondary font-medium">
                            <Award size={13} /> {lang === 'fr' ? `Objectif équipe — ${new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { month: 'long', year: 'numeric' })}` : `Team goal — ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
                        </div>
                        {teamGoal.is_admin && (
                            <Button size="sm" variant="outline" onClick={() => { setGoalInput(String(teamGoal.goal || '')); setGoalDialog(true); }} data-testid="team-goal-edit-btn">
                                {teamGoal.goal ? (lang === 'fr' ? "Modifier l'objectif" : 'Edit goal') : (lang === 'fr' ? 'Fixer un objectif' : 'Set a goal')}
                            </Button>
                        )}
                    </div>
                    {teamGoal.goal ? (
                        <>
                        <div className="mt-4 flex flex-wrap items-center gap-6">
                            <div className="flex items-baseline gap-2">
                                <span className="font-heading text-4xl text-ink tabular-nums" data-testid="team-goal-count">{teamGoal.completed_this_month}</span>
                                <span className="text-muted-foreground text-sm">/ {teamGoal.goal} {lang === 'fr' ? 'leçons ce mois' : 'lessons this month'}</span>
                            </div>
                            <div className="flex-1 min-w-[200px]">
                                <div className="h-2.5 rounded-full bg-border overflow-hidden">
                                    <div className={`h-full transition-[width] duration-500 ${teamGoal.completed_this_month >= teamGoal.goal ? 'bg-secondary' : 'bg-primary'}`}
                                        style={{ width: `${Math.min(100, Math.round(teamGoal.completed_this_month / teamGoal.goal * 100))}%` }} />
                                </div>
                                <div className="mt-1.5 flex justify-between text-[11px] text-muted-foreground">
                                    <span>{Math.min(100, Math.round(teamGoal.completed_this_month / teamGoal.goal * 100))}% {lang === 'fr' ? 'atteint' : 'reached'}</span>
                                    <span>{teamGoal.active_learners} / {teamGoal.members_count} {lang === 'fr' ? 'membres actifs' : 'active members'}</span>
                                </div>
                            </div>
                            {teamGoal.completed_this_month >= teamGoal.goal && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-light text-secondary text-xs font-medium" data-testid="team-goal-reached">
                                    <Award size={13} /> {lang === 'fr' ? 'Objectif atteint !' : 'Goal reached!'}
                                </span>
                            )}
                        </div>
                        {teamGoal.history?.length > 1 && (
                            <div className="mt-5 pt-4 border-t border-border">
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{lang === 'fr' ? 'Historique — 6 derniers mois' : 'History — last 6 months'}</div>
                                <div className="flex flex-wrap gap-2" data-testid="team-goal-history">
                                    {teamGoal.history.map(h => (
                                        <div key={h.month}
                                            className={`rounded-lg border px-3 py-2 text-center min-w-[76px] ${h.current ? 'border-primary/50 bg-primary-light/30' : h.reached === true ? 'border-secondary/40 bg-secondary-light/40' : h.reached === false ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-surface-muted/40'}`}
                                            data-testid={`goal-month-${h.month}`}>
                                            <div className="text-[11px] text-muted-foreground capitalize">{new Date(`${h.month}-02`).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', year: '2-digit' })}</div>
                                            <div className="font-heading text-lg tabular-nums">{h.completed}<span className="text-xs text-muted-foreground">/{teamGoal.goal}</span></div>
                                            <div className={`text-[10px] font-medium ${h.current ? 'text-primary' : h.reached ? 'text-secondary' : 'text-destructive'}`}>
                                                {h.current ? (lang === 'fr' ? 'En cours' : 'Ongoing') : h.reached ? `✓ ${lang === 'fr' ? 'Atteint' : 'Reached'}` : `✗ ${lang === 'fr' ? 'Manqué' : 'Missed'}`}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        </>
                    ) : (
                        <p className="mt-3 text-sm text-foreground/60" data-testid="team-goal-empty">
                            {teamGoal.is_admin
                                ? (lang === 'fr' ? "Aucun objectif défini. Fixez un nombre de leçons à compléter par l'équipe chaque mois." : 'No goal set. Define a monthly team lesson target.')
                                : (lang === 'fr' ? "Aucun objectif mensuel défini par l'admin de l'espace pour le moment." : 'No monthly goal set by the workspace admin yet.')}
                            {' '}{teamGoal.completed_this_month > 0 && (lang === 'fr' ? `Déjà ${teamGoal.completed_this_month} leçon(s) complétée(s) ce mois.` : `${teamGoal.completed_this_month} lesson(s) already completed this month.`)}
                        </p>
                    )}
                </section>
            )}

            {/* REPRENDRE LA LECTURE */}
            {resumeLessons.length > 0 && (
                <section className="rounded-2xl border border-amber-200/70 bg-amber-50/50 p-5" data-testid="campus-resume-section">
                    <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.25em] text-amber-700 font-medium">
                        <Clock size={13} /> {lang === 'fr' ? 'Reprendre la lecture' : 'Resume reading'}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {resumeLessons.map(r => (
                            <Link key={r.id} to={`/app/formation/lecon/${r.id}`}
                                className="group flex items-center gap-3 rounded-xl bg-white border border-border hover:border-amber-400 px-4 py-2.5 transition-colors max-w-full"
                                data-testid={`campus-resume-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-ink truncate max-w-[260px]">{r.title}</div>
                                    <div className="mt-1.5 flex items-center gap-2">
                                        <div className="h-1 w-24 rounded-full bg-amber-100 overflow-hidden">
                                            <div className="h-full bg-amber-500" style={{ width: `${r.pct}%` }} />
                                        </div>
                                        <span className="text-[11px] text-amber-700 font-medium tabular-nums">{r.pct}%</span>
                                    </div>
                                </div>
                                <ArrowRight size={15} className="text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* LEÇONS POPULAIRES */}
            {popular.length > 0 && (
                <section className="rounded-2xl border border-border bg-surface-muted/40 p-5" data-testid="campus-popular-section">
                    <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.25em] text-primary font-medium">
                        <TrendingUp size={13} /> {lang === 'fr' ? 'Leçons populaires — les plus recommandées par les équipes' : 'Popular lessons — most recommended by teams'}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {popular.map((p, i) => (
                            <Link key={p.id} to={`/app/formation/lecon/${p.id}`}
                                className="group flex items-center gap-3 rounded-xl bg-white border border-border hover:border-primary/50 px-4 py-2.5 transition-colors max-w-full"
                                data-testid={`campus-popular-${p.id}`}>
                                <span className="font-heading text-lg text-primary/40 tabular-nums">#{i + 1}</span>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-ink truncate max-w-[260px]">{(lang === 'fr' ? p.title_fr : p.title_en) || p.title_fr}</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        {p.recommendations} {lang === 'fr' ? (p.recommendations > 1 ? 'recommandations' : 'recommandation') : (p.recommendations > 1 ? 'recommendations' : 'recommendation')}
                                        {p.duration_min ? ` · ${p.duration_min} min` : ''}
                                    </div>
                                </div>
                                <ArrowRight size={15} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* TRONC COMMUN */}
            <section data-testid="campus-pillars-section">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
                    <div>
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-primary font-medium mb-3">
                            <span className="h-px w-8 bg-primary/40" /> {lang === 'fr' ? 'Tronc commun — 10 piliers' : 'Core curriculum — 10 pillars'}
                        </div>
                        <h2 className="font-heading text-4xl lg:text-5xl leading-tight">
                            African Valley <span className="text-primary">Core Framework</span>
                        </h2>
                        <p className="text-foreground/65 mt-2 max-w-2xl">
                            {lang === 'fr'
                                ? 'La méthodologie propriétaire pour structurer, financer, piloter et transformer tout projet africain.'
                                : 'The proprietary methodology to structure, finance, steer and transform any African project.'}
                        </p>
                    </div>
                    {progress && pillars.length > 0 && (
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{lang === 'fr' ? 'Leçons complétées' : 'Lessons completed'}</div>
                            <div className="font-heading text-3xl text-primary tabular-nums">{progress.lessons_completed}<span className="text-foreground/30">/{progress.lessons_total || '—'}</span></div>
                        </div>
                    )}
                </div>
                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={i} className="h-44 rounded-2xl bg-surface-muted animate-pulse" />
                        ))}
                    </div>
                ) : pillars.length === 0 ? (
                    <div className="card-soft p-8 text-center text-foreground/60" data-testid="pillars-empty">
                        {lang === 'fr'
                            ? 'Aucun pilier publié pour le moment. Le Super Admin doit créer le contenu depuis l\'espace d\'édition.'
                            : 'No pillars published yet. Super Admin must create content from the editing space.'}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        {pillars.map((p) => {
                            const prog = progressByPillar[p.id] || { percent: 0, completed: 0, total: 0 };
                            return (
                                <Link
                                    key={p.id}
                                    to={`/app/formation/pilier/${p.slug}`}
                                    className="group relative overflow-hidden rounded-2xl border border-border bg-surface p-5 hover:border-primary/40 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                                    data-testid={`pillar-card-${p.slug}`}
                                    style={{ '--accent': p.color_hex }}
                                >
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="h-11 w-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                                             style={{ background: `${p.color_hex}1A`, color: p.color_hex }}>
                                            <PillarIcon name={p.icon} size={20} strokeWidth={1.6} />
                                        </div>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">P{p.order_index}</span>
                                    </div>
                                    <h3 className="font-heading text-lg leading-tight mb-1.5">
                                        {lang === 'fr' ? p.title_fr : p.title_en}
                                    </h3>
                                    <p className="text-xs text-foreground/60 line-clamp-2 mb-4">
                                        {lang === 'fr' ? p.summary_fr : p.summary_en}
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 mr-3">
                                            <div className="h-1 rounded-full bg-surface-muted overflow-hidden">
                                                <div className="h-full transition-all duration-700"
                                                     style={{ width: `${prog.percent}%`, background: p.color_hex }} />
                                            </div>
                                            <div className="text-[10px] text-muted-foreground mt-1">{prog.completed}/{prog.total} {lang === 'fr' ? 'leçons' : 'lessons'}</div>
                                        </div>
                                        <ArrowRight size={14} className="text-primary group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* CAS D'USAGE SECTORIELS */}
            <section data-testid="campus-usecases-section">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
                    <div>
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-secondary font-medium mb-3">
                            <span className="h-px w-8 bg-secondary/40" /> {lang === 'fr' ? 'Cas d\'usage sectoriels — 14 verticales' : 'Sectoral use cases — 14 verticals'}
                        </div>
                        <h2 className="font-heading text-4xl lg:text-5xl leading-tight">
                            {lang === 'fr' ? <>Applications <span className="text-secondary">terrain</span> par secteur</> : <>Field <span className="text-secondary">applications</span> by sector</>}
                        </h2>
                        <p className="text-foreground/65 mt-2 max-w-2xl">
                            {lang === 'fr'
                                ? 'Énergie, Santé, Agribusiness, Mines, Logistique, Fintech… 14 verticales avec KPIs, modèles financiers et risques spécifiques.'
                                : 'Energy, Health, Agribusiness, Mining, Logistics, Fintech… 14 verticals with specific KPIs, models and risks.'}
                        </p>
                    </div>
                </div>
                {useCases.length === 0 ? (
                    <div className="card-soft p-8 text-center text-foreground/60">
                        {lang === 'fr' ? 'Aucun cas d\'usage disponible.' : 'No use case available.'}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {useCases.map((uc) => (
                            <Link
                                key={uc.id}
                                to={`/app/formation/${uc.slug}`}
                                className="card-soft overflow-hidden flex flex-col group hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
                                data-testid={`usecase-card-${uc.slug}`}
                            >
                                <div className="h-40 bg-surface-muted overflow-hidden relative">
                                    {uc.hero_url ? (
                                        <img src={uc.hero_url} alt={uc.title_fr}
                                             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                                            <span className="font-heading text-5xl text-primary/30">{uc.sector?.[0] || 'A'}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="p-5 flex-1 flex flex-col">
                                    <div className="eyebrow mb-2 text-secondary">{uc.sector}</div>
                                    <h3 className="font-heading text-xl mb-2 leading-tight">{lang === 'fr' ? uc.title_fr : uc.title_en}</h3>
                                    <p className="text-foreground/70 text-sm flex-1">{lang === 'fr' ? uc.summary_fr : uc.summary_en}</p>
                                    <div className="mt-4 flex items-center justify-between">
                                        <div className="inline-flex items-center text-xs text-muted-foreground">
                                            <Clock size={12} className="mr-1.5" /> {uc.duration_minutes || 30} min
                                        </div>
                                        <ArrowRight size={14} className="text-primary group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {/* Footer CTA */}
            <section className="rounded-2xl border border-border bg-gradient-to-br from-surface-muted/60 to-primary/5 p-6 flex flex-wrap items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Award size={26} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <div className="font-heading text-xl mb-1">
                        {lang === 'fr' ? 'Certifications délivrées' : 'Certifications delivered'}
                    </div>
                    <div className="text-sm text-foreground/70 leading-relaxed">
                        {lang === 'fr'
                            ? 'Validez vos quiz à 70 % minimum et téléchargez vos certificats African Valley Campus 360° — avec QR code de vérification publique.'
                            : 'Pass quizzes at 70%+ and download your African Valley Campus 360° certificates — with public QR verification.'}
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">Pilier</span>
                    <span className="px-2 py-1 rounded-full bg-secondary/10 text-secondary font-medium">Tronc</span>
                    <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">Use Case</span>
                </div>
            </section>

            {/* Dialog objectif équipe */}
            <Dialog open={goalDialog} onOpenChange={setGoalDialog}>
                <DialogContent className="max-w-sm" data-testid="team-goal-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">{lang === 'fr' ? 'Objectif mensuel de formation' : 'Monthly training goal'}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-foreground/60">{lang === 'fr' ? "Nombre de leçons à compléter par l'équipe chaque mois (0 pour désactiver)." : 'Lessons the team should complete each month (0 to disable).'}</p>
                    <Input type="number" min="0" max="1000" value={goalInput} onChange={(e) => setGoalInput(e.target.value)}
                        placeholder="Ex : 10" data-testid="team-goal-input" />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setGoalDialog(false)}>{lang === 'fr' ? 'Annuler' : 'Cancel'}</Button>
                        <Button onClick={saveGoal} disabled={savingGoal} className="bg-primary hover:bg-primary-hover" data-testid="team-goal-save">
                            {savingGoal ? <Loader2 size={14} className="animate-spin" /> : (lang === 'fr' ? 'Enregistrer' : 'Save')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
